// Per-voice brightness/scale pulse driven by RMS. Pure, deterministic, no browser API.

import { clamp, smoothstep } from './math';

/** Attack time constant (seconds): fast rise so the flash is immediate. */
export const PULSE_ATTACK_TAU = 0.04;
/** Release time constant (seconds): slow decay to mirror modal ring-down. */
export const PULSE_RELEASE_TAU = 0.45;

/** Live pulse state for one sprite/voice. Mutated in place each frame. */
export interface PulseState {
  /** 0..1 current pulse value. */
  pulse: number;
}

export function createPulseState(): PulseState {
  return { pulse: 0 };
}

/** Map raw smoothed RMS (0..~1) to the pulse target (0..1). */
export function pulseTarget(rms: number): number {
  return smoothstep(0, 0.7, rms);
}

/**
 * Advance a pulse toward `target` by `dtSec` using an asymmetric one-pole.
 * Attack (target>current) uses the fast tau; release uses the slow tau, so
 * loud hits flash quickly then linger. Returns the new pulse (also written to state).
 */
export function updatePulse(state: PulseState, target: number, dtSec: number): number {
  const rising = target > state.pulse;
  const tau = rising ? PULSE_ATTACK_TAU : PULSE_RELEASE_TAU;
  // 1 - exp(-dt/tau); guard dt<=0.
  const k = dtSec <= 0 ? 0 : 1 - Math.exp(-dtSec / tau);
  state.pulse += (target - state.pulse) * k;
  return state.pulse;
}

/**
 * On a strike, floor the pulse to max(pulse, 0.85*energy) for a synchronous flash
 * that precedes the engine's RMS read-back. Returns the new pulse.
 */
export function strikeFloor(state: PulseState, energy: number): number {
  const floor = 0.85 * clamp(energy, 0, 1);
  if (floor > state.pulse) state.pulse = floor;
  return state.pulse;
}

/** Scale multiplier from pulse + breathing: baseScale*(1 + 0.18*pulse)*breath. */
export function pulseScale(base: number, pulse: number, breath = 1): number {
  return base * (1 + 0.18 * pulse) * breath;
}

/** Brightness multiplier from pulse (1 + 0.6*pulse): drives sprite body alpha/glow. */
export function pulseIntensity(pulse: number): number {
  return 1 + 0.6 * pulse;
}
